import { useState } from 'react'
import cx from 'classnames'
import { Star, StarFilled } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useRateMessage } from '../../../../hooks/queries/chat/useRateMessage'
import { StarRatingFeedbackForm } from './StarRatingFeedbackForm'
import type { ChatMessage, ChatRatingContent } from '../../../../api/clients/ChatClient'
import Styles from './StarRating.module.scss'

const STAR_COLOR_FILLED = '#F5A623'
const STAR_COLOR_EMPTY = 'rgba(0, 0, 0, 0.25)'

const normalizeRating = (rating: ChatRatingContent['rating']): number => {
  if (typeof rating === 'number') return rating
  if (rating === 'upvote') return 5
  if (rating === 'downvote') return 1
  return 0
}

interface StarRatingProps {
  message: ChatMessage
}

const StarRating = ({ message }: StarRatingProps) => {
  const { mutate: rateMessage } = useRateMessage()
  const [hoveredStar, setHoveredStar] = useState<number | null>(null)
  const [selectedRating, setSelectedRating] = useState<number | null>(() => {
    const ratedContent = message.content?.find((c): c is ChatRatingContent => c.type === 'rated')
    if (!ratedContent) return null
    return normalizeRating(ratedContent.rating)
  })
  const [hasSubmitted, setHasSubmitted] = useState(() => selectedRating !== null)

  const showFeedbackForm = selectedRating !== null && selectedRating <= 2 && !hasSubmitted

  const submitRating = (issueType?: string, comment?: string) => {
    if (selectedRating === null) return
    setHasSubmitted(true)
    rateMessage({
      threadId: message.thread_id,
      messageId: message.id,
      rating: selectedRating,
      issueType,
      comment,
    })
  }

  const handleStarClick = (star: number) => {
    if (hasSubmitted) return
    setSelectedRating(star)

    if (star > 2) {
      setHasSubmitted(true)
      rateMessage({
        threadId: message.thread_id,
        messageId: message.id,
        rating: star,
      })
    }
  }

  const displayRating = hoveredStar ?? selectedRating ?? 0

  const starsRow = (
    <div className={Styles.starsRow}>
      <Text variant="secondary" color="var(--dark-40)">
        How was the result?
      </Text>
      <div className={Styles.starsContainer} onMouseLeave={() => !hasSubmitted && setHoveredStar(null)}>
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= displayRating
          const StarIcon = isFilled ? StarFilled : Star
          return (
            <button
              key={star}
              type="button"
              disabled={hasSubmitted}
              onClick={() => handleStarClick(star)}
              onMouseEnter={() => !hasSubmitted && setHoveredStar(star)}
              className={cx(Styles.starButton, { [Styles.disabled]: hasSubmitted })}
              aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
            >
              <StarIcon size={14} color={isFilled ? STAR_COLOR_FILLED : STAR_COLOR_EMPTY} />
            </button>
          )
        })}
      </div>
    </div>
  )

  if (showFeedbackForm) {
    return (
      <div className={Styles.feedbackContainer}>
        {starsRow}
        <StarRatingFeedbackForm onSubmit={submitRating} onSkip={() => submitRating()} />
      </div>
    )
  }

  return starsRow
}

export default StarRating
